import React from 'react';
import { Button, Dialog, Group, Switch } from '@economic/taco';
import { Trans } from '@lingui/macro';
import { useField } from 'react-final-form';
import ExternalLink from '~/app/shared/components/ExternalLink';
import PaymentMethodBadge from '~/app/pages/market/onboarding/CustomerPayments/components/PaymentMethodBadge';
import mastercard from '~/app/pages/market/onboarding/CustomerPayments/assets/mastercard.png';
import visa from '~/app/pages/market/onboarding/CustomerPayments/assets/visa.png';
import googlepay from '~/app/pages/market/onboarding/CustomerPayments/assets/googlepay.png';
import applepay from '~/app/pages/market/onboarding/CustomerPayments/assets/applepay.png';
import mobilepay from '~/app/pages/market/onboarding/CustomerPayments/assets/mobilepay.png';
import DK from '~/app/pages/market/onboarding/CustomerPayments/assets/DK.png';
import { Field } from '~/app/shared/components/Form';

interface Props {
    prevStep: () => void;
    nextStep: () => void;
}

const PricePlan = ({ prevStep, nextStep }: Props) => {
    const fieldPricePlan = useField('pricePlan');

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        nextStep();
    };

    return (
        <>
            <Dialog.Title>
                <Trans>Choose the price plan</Trans>
            </Dialog.Title>

            <div className="flex my-4">
                <Trans>Select the payment methods you want to add to your invoices.</Trans>
            </div>

            <div className="border border-grey-200 rounded-lg p-4 divide-y divide-grey-200">
                <section>
                    <div className="flex">
                        <strong>
                            <Trans>Fee: 1.5% + DKK 2.5 per transaction. No hidden costs.</Trans>
                        </strong>
                    </div>

                    <div className="flex my-4">
                        <Group>
                            <PaymentMethodBadge imgSrc={mastercard} alt="mastercard" />
                            <PaymentMethodBadge imgSrc={visa} alt="visa" />
                            <PaymentMethodBadge imgSrc={DK} alt="DK" />
                            <PaymentMethodBadge imgSrc={googlepay} alt="googlepay" />
                            <PaymentMethodBadge imgSrc={applepay} alt="applepay" />
                        </Group>
                    </div>
                </section>

                <section>
                    <div className="flex mt-4 align-center">
                        <span className="self-center pr-2">
                            <Field.Checkbox name="pricePlan" fieldClassName="flex">
                                {({ input: { name, value, onChange } }) => (
                                    <>
                                        <Switch
                                            className="!mt-0 mr-2"
                                            name={name}
                                            checked={!!value}
                                            onChange={value => {
                                                onChange(Number(value));
                                            }}
                                        />
                                        <Trans>Add</Trans>
                                    </>
                                )}
                            </Field.Checkbox>
                        </span>
                        <PaymentMethodBadge imgSrc={mobilepay} alt="mobilepay" />
                    </div>

                    <span className={fieldPricePlan.input.value ? '' : '!text-grey-500'}>
                        <div className="flex mt-4">
                            <Trans>Add MobilePay as a payment method for your invoices.</Trans>
                        </div>

                        <div className="flex flex-col my-4">
                            <strong>
                                <Trans>Price: 49 DKK/month. (MobilePay subscription)*</Trans>
                            </strong>
                            <strong>
                                <Trans>Fee: 1.5% + 3.5 DKK per transaction.</Trans>
                            </strong>
                        </div>

                        <Trans>*The subscription price is charged by e-conomic.</Trans>
                    </span>
                </section>
            </div>

            <div className="flex mt-10">
                <span className="flex items-center">
                    <span className="mr-1">
                        <Trans>Read more about the pricing</Trans>
                    </span>

                    <ExternalLink href="#Terms-and-Conditions">
                        <Trans>here.</Trans>
                    </ExternalLink>
                </span>
                <Group className="flex ml-auto">
                    <Button onClick={onPrevClick}>
                        <Trans>Back</Trans>
                    </Button>
                    <Button appearance="primary" onClick={onNextClick}>
                        <Trans>Next</Trans>
                    </Button>
                </Group>
            </div>
        </>
    );
};

export default PricePlan;
